import React, { useCallback, useState } from 'react';
import { trackEvent } from 'utils/analytics';
import { HTTPError } from 'clients/HTTPClient';
import { transactionReportsClient } from 'clients';
import { revenueRentalType, RevenueRentalType } from 'types/TransactionReport';
import { SpotInterface } from 'types';
import DownloadIcon from 'assets/icons/download.svg?react';
import { RentalType } from 'utils/constants';
import { getAllTimeStartDate, getStartDateFromFilter, Period, Timeframe } from 'components/RevenueWidgets/helpers';
import Button from 'components/Button/Button';
import AGMessage from 'components/AGMessage/AGMessage';
import styles from './DownloadReport.module.css';

function CsvDownloadRow({
    title,
    onClick,
    disabled = false,
}: {
    title: string;
    onClick: () => void;
    disabled: boolean;
}) {
    return (
        <div className={styles.buttonRow}>
            <p>{title}</p>
            <Button onClick={onClick} disabled={disabled} size="sm" color="info">
                <DownloadIcon /> Download
            </Button>
        </div>
    );
}

type CSVModalProps = {
    spot: SpotInterface;
    filters: {
        timeframe: Timeframe;
        dailyPeriod: Period;
        rentalType: RevenueRentalType;
        month: string;
    };
};

function CSVModal({ spot, filters }: CSVModalProps): JSX.Element {
    const [loadingCSV, setLoadingCSV] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const csvDownloadWrapper = useCallback(
        async (downloadFunction: () => Promise<void>) => {
            setLoadingCSV(true);
            try {
                await downloadFunction();
            } catch (e) {
                if (HTTPError.isHTTPError(e)) {
                    setErrorMessage(e.message);
                } else {
                    setErrorMessage(e?.toString() || 'Something went wrong');
                }
            } finally {
                setLoadingCSV(false);
            }
        },
        [setErrorMessage, setLoadingCSV]
    );

    const downloadRevenueCsv = useCallback(() => {
        csvDownloadWrapper(async () => {
            const isDaily = filters.timeframe === Timeframe.DAILY;
            const isAllTime = filters.timeframe === Timeframe.ALL_TIME;
            const momSuffix = isAllTime ? 'MoM-AllTime' : 'MoM';
            const fileName = `${spot.name}_${isDaily ? `DoD${filters.dailyPeriod}` : momSuffix}.csv`.replace(/ /g, '-');
            if (isDaily) {
                const startDate = getStartDateFromFilter(filters.dailyPeriod);
                await transactionReportsClient.getRevenueDailyReportCSV(spot.uuid, fileName, startDate);
                trackEvent('Download DoD CSV', {
                    spot: spot.pk,
                    spotName: spot.name,
                    dailyPeriod: filters.dailyPeriod,
                    type: 'csv',
                });
            } else {
                const startDate = isAllTime ? getAllTimeStartDate(spot) : undefined;
                await transactionReportsClient.getRevenueMoMReportCSV(spot.uuid, fileName, startDate);
                trackEvent('Download MoM CSV', {
                    spot: spot.pk,
                    spotName: spot.name,
                    allTime: isAllTime,
                    type: 'csv',
                });
            }
        });
    }, [filters.timeframe, filters.dailyPeriod, spot.name, spot.pk, csvDownloadWrapper]);

    const downloadRentalTypeCsv = useCallback(() => {
        csvDownloadWrapper(async () => {
            const fileName = `${spot.name}_revenueByType.csv`.replace(/ /g, '-');
            const rentalTypes = filters.rentalType.replace(revenueRentalType.FLAT_RATE, RentalType.Flat);
            await transactionReportsClient.getRevenueByRentalTypeCSV(spot.uuid, rentalTypes, fileName);
            trackEvent('Download Rental Type CSV', {
                spot: spot.pk,
                spotName: spot.name,
                rentalType: filters.rentalType,
                type: 'csv',
            });
        });
    }, [filters.rentalType, spot.name, spot.pk, csvDownloadWrapper]);

    const downloadHistoricalCsv = useCallback(() => {
        csvDownloadWrapper(async () => {
            const fileName = `${spot.name}_historical_${filters.month}.csv`.replace(/ /g, '-');
            await transactionReportsClient.getYoYRevenueReportCSV(spot.uuid, filters.month, fileName);
            trackEvent('Download YoY CSV', {
                spot: spot.pk,
                spotName: spot.name,
                month: filters.month,
                type: 'csv',
            });
        });
    }, [filters.month, spot.name, spot.pk, csvDownloadWrapper]);

    const downloadPacingCsv = useCallback(() => {
        csvDownloadWrapper(async () => {
            const fileName = `${spot.name}_pacing.csv`.replace(/ /g, '-');
            await transactionReportsClient.getPacingReportCSV(spot.uuid, fileName);
            trackEvent('Download Pacing CSV', {
                spot: spot.pk,
                spotName: spot.name,
                type: 'csv',
            });
        });
    }, [spot.name, spot.pk, csvDownloadWrapper]);

    return (
        <div>
            <p className={styles.title}>Get a CSV file for each section</p>
            {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}

            <CsvDownloadRow title="Revenue" onClick={downloadRevenueCsv} disabled={loadingCSV} />
            <CsvDownloadRow
                title="Gross Revenue by Rental Type"
                onClick={downloadRentalTypeCsv}
                disabled={loadingCSV}
            />
            <CsvDownloadRow title="Historical Comparisons" onClick={downloadHistoricalCsv} disabled={loadingCSV} />
            <CsvDownloadRow title="Current Month Pacing" onClick={downloadPacingCsv} disabled={loadingCSV} />
        </div>
    );
}

export default CSVModal;
